import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { parseBrowser } from 'ctm-util-functions';
import { SnippetTemplates } from './snippet_templates';

// @todo: this file is a complete nightmare - thanks again Jose... - refactor one day, this should only have about of the 1/4 of the current code

/**
 * A snippet model (semi-builder) that creates snippet markup and returns it as a string,
 * based on a snippet schema and the current url.
 */
class SnippetModel {

    /**
     * Construct the class and start the building of snippets.
     * @param {Object} schema The snippet model schema
     */
    constructor(schema) {
        this.snippetAttribute = 'data-ctm-snippet';
        this.schema = schema;
        this.snippetsDictionary = {};
        this.pageSnippets = {};
        this.builSnippetsDictionary();
    }

    /**
     * Build the snippets lookup dictionary using the snippet name as its key.
     * @todo: the dictionary wouldn't be necessary if the snippet schema is updated to be an associative array.
     */
    builSnippetsDictionary = () => {
        this.schema.snippets.forEach((snippet, index, snippets) => {
            this.snippetsDictionary[this.schema.snippets[index].name] = snippets[index];
        });
    };

    /**
     * Based on the snippet schema properties, return a HTML string of the template, with
     * its properties replaced, and wrapped in its wrapping node.
     * @example <noscript><iframe template...></noscript>
     * @param {Object} snippetSchema An individual schema object.
     * @returns {String} HTML
     */
    buildSnippet = (snippetSchema) => {
        const snippetProperties = snippetSchema.properties || {};
        const snippetAttributes = snippetSchema.attributes || {};
        const templateName = snippetSchema.template || snippetSchema.name;
        const template = SnippetTemplates[templateName] ? SnippetTemplates[templateName](snippetProperties) : null;

        const wrapperTagName = snippetSchema.wrappingNode;

        // return the template if no wrapping Node tag was set.
        if (!wrapperTagName) {
            return template;
        }

        // Prepare properties for the new element.
        const props = snippetSchema.type === 'library' ? snippetProperties : snippetAttributes;
        props[this.snippetAttribute] = true;
        props.dangerouslySetInnerHTML = { __html: template };
        const customTag = React.createElement(wrapperTagName, props);

        return ReactDOMServer.renderToStaticMarkup(customTag);
    };

    /**
     * Remove the snippets added by the app to the DOM.
     */
    cleanPageSnippets = () => {
        // similar to the react-helmet updateTags method
        if (typeof window !== 'undefined') {
            // Get a NodeList of all matching elements.
            const tagNodes = document.querySelectorAll(`[${this.snippetAttribute}]`);
            if (tagNodes && tagNodes.length) {
                // Some browsers don't supports forEach() iterator methods for NodeList objects.
                for (const tagNode of tagNodes) {
                    if (tagNode instanceof Node) {
                        tagNode.parentNode.removeChild(tagNode);
                    }
                }
            }
        }
    };

    updatesPageSnippets = () => {
        /**
        * @todo add a method to append the snippets to the DOM after cleaning the old ones.
        * Important! Even the snippets are removed, its side effects still persist. This happens also adding scripts using react helmet.
        */
        this.cleanPageSnippets();
    };

    /**
     * Append all snippets in the schema to the object so it can be output in the full page render.
     * @todo allow to append text for SSR "or" DOM elements for the client side.
     * @param  {Array} snippetNames  - Array of snippet names.
     * @param {String} userAgent - the current browser user agent
     */
    buildPageSnippets = (snippetNames = [], userAgent) => {
        // this is necessary to avoid caching when refreshing.
        this.pageSnippets = {};
        // get the browserDetails
        const parsedUA = parseBrowser(userAgent);

        snippetNames.forEach((snippetName) => {
            if (parsedUA && parsedUA.Agent && this.snippetsDictionary[snippetName]) {
                const snippet = this.snippetsDictionary[snippetName];

                // @todo: refactor the logic here to avoid calling appendSnippet in 2 places
                if (snippet.browsers) {
                    snippet.browsers.forEach((browser) => {
                        // Check if the browser's name is similar and the version matches the regex
                        if ((browser.name === parsedUA.Agent.browser) && parsedUA.checkBrowserVersionMask(browser.versionRegex)) {
                            this.appendSnippet(snippet, 'pageSnippets');
                        }
                    });
                } else {
                    this.appendSnippet(snippet, 'pageSnippets');
                }
            }
        });
    };

    /**
     * Append the snippet if it is valid for the environment.
     * @param  {Object} snippet  - Object containing the snippet details.
     * @param  {String} attrName - Name of attribute name where to append the snippet.
     */
    appendSnippet = (snippet, attrName) => {
        if (snippet.environments && process.env.EVEREST_DEPLOY_ENV && (snippet.environments.indexOf(process.env.EVEREST_DEPLOY_ENV.trim()) !== -1)) {
            if (!this[attrName][snippet.type]) {
                this[attrName][snippet.type] = [];
            }

            if (!this[attrName][snippet.type][snippet.placement]) {
                this[attrName][snippet.type][snippet.placement] = [];
            }

            this[attrName][snippet.type][snippet.placement].push(this.buildSnippet(snippet));
        }
    };

    /**
     * Returns a string of all the snippet templates set to the Head section for the current page.
     * @returns {string|*} Body Scripts
     */
    getPageHeadSnippets = () => {
        return this.getSnippets('head', 'link', 'library', 'embeddedCode');
    };

    /**
     * Returns a string of all the snippet templates set to the Body section for the current page.
     * @returns {string|*} Body Scripts
     */
    getPageBodySnippets = () => {
        return this.getSnippets('body', 'link', 'library', 'embeddedCode');
    };

    /**
     * Returns a string of all library snippet templates for the Head section for the current page.
     * @returns {string|*} Head scripts
     */
    getPageHeadLibrarySnippets = () => {
        return this.getSnippets('head', 'library');
    };

    /**
     * Returns a string of all link snippet templates for the Head section for the current page.
     * @returns {string|*} Head scripts
     */
    getPageHeadLinkSnippets = () => {
        return this.getSnippets('head', 'link');
    };

    /**
     * Returns a string of all embeddedCode snippet templates for the Head section for the current page.
     * @returns {string|*} Body Scripts
     */
    getPageHeadEmbeddedSnippets = () => {
        return this.getSnippets('head', 'embeddedCode');
    };

    /**
     * Returns a string of all embeddedCode snippet templates for the Body section for the current page.
     * @returns {string|*} Body Scripts
     */
    getPageBodyEmbeddedSnippets = () => {
        return this.getSnippets('body', 'embeddedCode');
    };

    /**
     * Returns a string of all embeddedCode snippet templates for the Body section for the current page.
     * @returns {string|*} Body Scripts
     */
    getPageBodyLibrarySnippets = () => {
        return this.getSnippets('body', 'library');
    };

    /**
     * Returns a string of all library snippet templates for the end of the body section for the current page.
     * @returns {string|*} End of Page Libraries
     */
    getPageEndLibrarySnippets = () => {
        return this.getSnippets('end', 'library');
    };

    /**
     * Returns a string of all embedded snippet templates for the end of the body section for the current page.
     * @returns {string|*} End of Page Scripts
     */
    getPageEndEmbeddedSnippets = () => {
        return this.getSnippets('end', 'embeddedCode');
    };

    /**
     * Returns a string of all link snippet templates for the end of the body section for the current page.
     * @returns {string|*} End of Page Scripts
     */
    getPageEndLinkSnippets = () => {
        return this.getSnippets('end', 'link');
    };

    /**
     * Get the snippet for given section and snippet types.
     * @param  {String} section         - Section of the snippet.
     * @param  {...string} snippetTypes - Type of the snippet.
     * @returns {String}                 - joined snippets.
     */
    getSnippets = (section, ...snippetTypes) => {
        const snippets = [];
        for (const snippetType of snippetTypes) {
            if (this.pageSnippets[snippetType] && this.pageSnippets[snippetType][section]) {
                snippets.push(this.pageSnippets[snippetType][section].join('\n'));
            }
        }

        return snippets.join('\n');
    };

}

export { SnippetModel };
